import cx from "classnames";
import isNumber from "lodash-es/isNumber";
import range from "lodash-es/range";
import truncate from "lodash-es/truncate";
import { parse, stringify } from "query-string";
import React from "react";
import { Helmet } from "react-helmet";

import Comment, { CommentForumPlaceholder } from "@/mk2/apps/blogs/components/Comment";
import { counsellingsHomeUrl } from "@/mk2/apps/counsellings/urls";
import { CommentFormData } from "@/mk2/apps/forum/components/CommentCreateEditForm";
import ForumCommentAdd from "@/mk2/apps/forum/components/ForumCommentAdd";
import Post from "@/mk2/apps/forum/components/Post";
import { PostDetailRelatedV2 } from "@/mk2/apps/forum/components/PostDetailRelatedV2";
import PostIsHoaxAlert from "@/mk2/apps/forum/components/PostIsHoaxAlert";
import PostPlaceholder from "@/mk2/apps/forum/components/PostPlaceholder";
import { TESTINGS_TOPIC_ID, UNCATEGORIZED_TOPIC_ID } from "@/mk2/apps/forum/constants";
import { ARTICLE_IDS_TO_HIDE_WHEN_PREGNANCY_NEWSLETTER_IS_SHOWN } from "@/mk2/apps/forum/containers/PostDetail/PostDetail.constants";
import { removeLinesWithLinksToArticle } from "@/mk2/apps/forum/containers/PostDetail/PostDetail.helpers";
import {
    PostDetailViewType,
    Props,
    ScrollToPosition,
} from "@/mk2/apps/forum/containers/PostDetail/PostDetailPage";
import { PostDetailRelated } from "@/mk2/apps/forum/containers/PostDetail/PostDetailRelated";
import { postDetailUrl, topicUrl } from "@/mk2/apps/forum/helpers";
import { forumTopicsUrl, forumUrl } from "@/mk2/apps/forum/urls";
import { getPageNumberInPost, largestAvailablePhotoVersionOfPost } from "@/mk2/apps/forum/utils";
import { gtmEvent } from "@/mk2/apps/users/utils";
import ReviewArticles, { LayoutType } from "@/mk2/apps/wiki/components/ReviewArticles";
import { WIKI_REVIEWS_ARTICLE_SLUG } from "@/mk2/apps/wiki/helpers";
import { pregnancyCalculatorUrl } from "@/mk2/apps/wiki/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { CoverBreadcrumbs } from "@/mk2/components/CoverBreadcrumbs";
import { Link } from "@/mk2/components/Link";
import { Pager } from "@/mk2/components/Pager";
import { Panel } from "@/mk2/components/Panel";
import { Failure } from "@/mk2/components/errors/Failure";
import { FormPageType } from "@/mk2/constants/enums";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import Complaint from "@/mk2/containers/Complaint/Complaint";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { PregnancyNewsletter } from "@/mk2/containers/PregnancyNewsletter/PregnancyNewsletter";
import { getAllPlaintext } from "@/mk2/helpers/article_ast/utils";
import { tupdate } from "@/mk2/helpers/types";
import { removePage1 } from "@/mk2/helpers/urls";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { subscribeJam, unsubscribeJam } from "@/mk2/services/jam";
import {
    ASK_AN_EXPERT,
    BACK_TO_TOPICS,
    COUNSELLING_IS_ARCHIVED,
    FORUM,
    FORUM_ADD_QUESTION,
    PREGNANCY_NEWSLETTER,
    PREGNANCY_NEWSLETTER_DESCRIPTION,
    PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR,
    PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR_01,
    PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR_AUTH,
    PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR_AUTH_01,
    PREGNANCY_WEEKS_BOX_01,
    PREGNANCY_WEEKS_BOX_02,
    REPORTED_COMMENTS,
    WIKI_REVIEWS,
} from "@/mk/autogenerated/translation";
import {
    ANONYMOUS_PSEUDONYM_AUTHOR_NAME,
    getAnonymousUserPseudonym,
    isAnonymousUser,
    isAuthenticated,
    isAuthenticatedAs,
} from "@/mk/bazaar/common/userUtils";
import { photoUrl } from "@/mk/photo/photoUrl";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

import styles from "./PostDetail.mscss";

export const PREGNANCY_WEEKS_URL = {
    201: "/som-tehotna/tehotenstvo-po-tyzdnoch/",
    202: "/jsem-tehotna/tehotenstvi-tyden-po-tydnu/",
};

interface OwnState {
    // null = SSR / before mount (banner not rendered yet)
    // true  = position B is below fold → banner goes inside Post (position A)
    // false = position B is above fold → banner goes between Post and comments (position B)
    isBannerAtPositionA: boolean | null;
}

class PostDetail extends React.PureComponent<Props, OwnState> {
    private textArea: HTMLTextAreaElement;
    private bannerPositionBRef = React.createRef<HTMLDivElement>();

    constructor(props: Props) {
        super(props);
        this.state = { isBannerAtPositionA: null };
    }

    public UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const nextPropsHash =
            nextProps.location && nextProps.location.hash ? nextProps.location.hash : "";
        if (nextPropsHash !== "" && nextProps.post) {
            const hashValue =
                nextPropsHash.indexOf("-") !== -1 ? nextPropsHash.split("-")[1] : null;

            if (hashValue === "last") {
                // TODO: maybe we can use this also for comment-id (.../#post-12345678)
                const reducerKey = `${nextProps.post?.topic?.slug}/${nextProps.post.slug}`;
                nextProps.onToggleCommentHighlight(
                    reducerKey,
                    hashValue === "last" ? hashValue : parseFloat(hashValue),
                );

                // remove hash from url(location)
                this.props.routeNavigate(
                    {
                        ...nextProps.location,
                        hash: "",
                    },
                    { replace: true },
                );
            }
        }
    }

    public componentDidMount() {
        const { counselling, numberOfPages, onScrollTo, onStoreEvent, onTrackEdit, pageRef, post } =
            this.props;

        if (this.isEditable && post) {
            // post este nemusi byt loadnuty
            onTrackEdit(post.id);
        }

        if (
            post &&
            numberOfPages !== null &&
            pageRef !== null &&
            isNumber(pageRef) &&
            pageRef === numberOfPages
        ) {
            subscribeJam(`forum:post:${post.id}`);
        }

        if (post && counselling) {
            onStoreEvent("counselling_visit", {
                counselling_name: post.topic?.slug,
                question: post.topic?.slug + "/" + post.slug,
            });
        }

        const query = parse(this.props.location.search);
        if (this.props.loadingState === LoadingState.SUCCESS_ON_SERVER) {
            if (query.scrollto) {
                onScrollTo(query.scrollto as ScrollToPosition);
            }
        }

        if (query.showCaroda) {
            gtmEvent("event_show_caroda");
        }

        this.determineBannerPosition();

        // GA4 user property for A/B test segmentation
        const { forumRelatedV2 } = this.props;
        if (typeof window !== "undefined" && forumRelatedV2) {
            const w = window as any;
            w.dataLayer = w.dataLayer || [];
            w.gtag =
                w.gtag ||
                function (...args) {
                    w.dataLayer.push(args);
                };
            w.gtag("set", "user_properties", {
                forum_related_v2: forumRelatedV2,
            });
        }
    }

    public componentDidUpdate(prevProps: Props) {
        const {
            counselling,
            history,
            loadingState,
            location,
            numberOfPages,
            onScrollTo,
            onStoreEvent,
            pageRef,
            post,
        } = this.props;

        if (post && numberOfPages != null && pageRef != null) {
            // We came to last page - so subscribe for updates
            if (isNumber(pageRef) && pageRef === numberOfPages) {
                subscribeJam(`forum:post:${post.id}`);
            } else {
                // We left last page - no need to listen for changes
                unsubscribeJam(`forum:post:${post.id}`);
            }
        }

        if (
            (counselling &&
                ((post && prevProps.post && post.id !== prevProps.post.id) ||
                    (post && !prevProps.post))) ||
            (counselling && !prevProps.counselling)
        ) {
            onStoreEvent("counselling_visit", {
                counselling_name: post?.topic?.slug,
                question: post?.topic?.slug + "/" + post?.slug,
            });
        }

        if (
            // zmenil sa post (url) alebo page z postu
            (location.pathname !== prevProps.location.pathname || pageRef !== prevProps.pageRef) &&
            // nevraciame sa cez 'Back'
            history.action !== "POP" &&
            // a nacitali sme s cache (LoadSwitch nespustil postDetailFetchTrigger)
            (loadingState === LoadingState.SUCCESS ||
                loadingState === LoadingState.SUCCESS_ON_SERVER)
        ) {
            // defautlne sa scrollne na konci fetchPostDetail sagy, ale ta sa nespusti
            // tak vykonaj scroll sideeffec
            const query = parse(location.search);
            if (query.scrollto) {
                onScrollTo(query.scrollto as ScrollToPosition);
            }
        }

        // Re-check banner position on post/page change (SPA navigation)
        if (prevProps.post?.id !== post?.id || prevProps.pageRef !== pageRef) {
            this.determineBannerPosition();
        }
    }

    public componentWillUnmount() {
        const { post, numberOfPages, pageRef } = this.props;

        if (post && numberOfPages !== null && pageRef != null && isNumber(pageRef)) {
            unsubscribeJam(`forum:post:${post.id}`);
        }
    }

    public render() {
        const {
            backlinksBox,
            comments,
            contest,
            counselling,
            currentFormPage,
            currentValues,
            forumRelatedV2,
            initialValues,
            isIOS,
            isMobile,
            isMyAnonymousPost,
            isTitleEditableOnceAgain,
            linkedReviewArticles,
            loadingState,
            location,
            numberOfPages,
            pageRef,
            post,
            postCanHavePregnancyNewsletter,
            recentPosts,
            requestUser,
            requestUserPermissions,
            similarPosts,
            userCanHavePregnancyNewsletter,
            viewType,
        } = this.props;

        const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") !== -1;
        const isModerator =
            post &&
            isAuthenticatedAs(
                requestUser,
                post.topic?.topicModerators?.map((tm) => tm.user) || post.topic?.moderators,
            );
        const isAuthor = post && isAuthenticatedAs(requestUser, post.author);
        const isAskAnExpert = viewType === PostDetailViewType.AskAnExpert;
        const isContest = viewType === PostDetailViewType.Contest;

        let role;
        if (isAdmin) {
            role = "admin";
        } else if (isModerator) {
            role = "moderator";
        } else if (isAuthor) {
            role = "author";
        }

        const canAdd = this.canAddComment();
        const showContestAnswers = this.showContestAnswers();
        const noBreadcrumbs = isContest || post?.topic?.id === UNCATEGORIZED_TOPIC_ID;

        const hasAds = !!post && !post.deletedAt && !post.isNoAds && !post.topic.isCommercial;

        return (
            <div className={styles.PostDetail}>
                {this.renderHelmet()}

                {currentFormPage === FormPageType.Main ? (
                    <>
                        {post ? (
                            <>
                                {noBreadcrumbs ? (
                                    <div className={styles.PostDetail__noBreadcrumbs} />
                                ) : (
                                    <CoverBreadcrumbs
                                        photo={post.coverPhoto || post.topic.coverPhoto}
                                        className={styles.PostDetail__breadcrumbs}
                                    >
                                        {isAskAnExpert && (
                                            <Link key={post.id} to={counsellingsHomeUrl}>
                                                {ASK_AN_EXPERT}
                                            </Link>
                                        )}
                                        {!isAskAnExpert && (
                                            <Link key={post.id} to={forumUrl}>
                                                {FORUM}
                                            </Link>
                                        )}
                                        <Link key={post.topic.name} to={topicUrl(post.topic)}>
                                            {post.topic.name}
                                        </Link>
                                    </CoverBreadcrumbs>
                                )}

                                {/* TODO: Change entity name to match post db entity */}
                                <Complaint
                                    id={post.id}
                                    entity={ComplaintTargetType.ForumThread}
                                    className={styles.PostDetail__complaint}
                                    scrollOffset={44 + 44 + 30 /* header + 30px spacing */}
                                    inForum
                                    inGroups={false}
                                />
                                <Post
                                    post={post}
                                    followLinks={post.topic.isCommercial || !!counselling}
                                    isMyAnonymousPost={isMyAnonymousPost}
                                    isAuthor={isAuthor}
                                    isAdmin={isAdmin}
                                    isTitleEditableOnceAgain={isTitleEditableOnceAgain}
                                    contest={contest}
                                    key={post.id}
                                    isMobile={isMobile}
                                    location={location}
                                    className={styles.PostDetail__post}
                                    role={role}
                                    onReply={this.handleOnPostReply}
                                    hasReply={canAdd}
                                    bannerAd={
                                        hasAds &&
                                        this.state.isBannerAtPositionA === true &&
                                        loadingState <= LoadingState.FAILURE &&
                                        (comments === null ||
                                            comments === undefined ||
                                            comments.length >= 3) && (
                                            <AdSlot
                                                zone={"Forum - Top 01 - Mobile"}
                                                location={location}
                                                allowDownscaling
                                            />
                                        )
                                    }
                                />
                                {post.isHoax && (
                                    <PostIsHoaxAlert className={styles.PostDetail__hoaxAlert} />
                                )}
                            </>
                        ) : loadingState === LoadingState.LOADING ? (
                            <PostPlaceholder isMobile={isMobile} />
                        ) : null}

                        {/* Anchor div for measuring position B; renders banner here when post is short (above fold) */}
                        <div ref={this.bannerPositionBRef}>
                            {hasAds &&
                                this.state.isBannerAtPositionA === false &&
                                loadingState <= LoadingState.FAILURE &&
                                (comments === null ||
                                    comments === undefined ||
                                    comments.length >= 3) && (
                                    <AdSlot
                                        zone={"Forum - Top 01 - Mobile"}
                                        location={location}
                                        allowDownscaling
                                        marginTopWithAd={10}
                                        marginBottomWithAd={10}
                                    />
                                )}
                        </div>

                        <LoadingSwitch
                            loadingState={loadingState}
                            onLoad={this.load}
                            onRenderInit={this.renderInit}
                            onRenderSuccess={this.renderSuccess}
                            onRenderFailure={this.renderFailure}
                            hasData={Boolean(
                                post &&
                                post.topic &&
                                comments &&
                                numberOfPages !== null &&
                                pageRef !== null,
                            )}
                        />
                    </>
                ) : null}

                {!isMobile && (
                    <ForumCommentAdd
                        canAdd={canAdd}
                        anonymousMode={isMyAnonymousPost}
                        hasBlueToolbar={!isMyAnonymousPost}
                        isEdit={false}
                        isMobile={isMobile}
                        isIOS={isIOS}
                        textareaRef={this.handleTextareaRef}
                        isAskAnExpert={isAskAnExpert}
                        post={post}
                        showContestAnswers={showContestAnswers}
                        isContest={isContest}
                        isBlogsComment={false}
                        isGroupsComment={false}
                        requestUser={requestUser}
                        requestUserPermissions={requestUserPermissions}
                        initialValues={initialValues}
                        currentValues={currentValues}
                        onAddCommentUnauthenticated={this.handleOnAddCommentUnauthenticated}
                        onSave={this.handleOnSaveComment}
                    />
                )}
                {isMobile &&
                    forumRelatedV2 === "new" &&
                    !isContest &&
                    !isAskAnExpert &&
                    (similarPosts || []).slice(9).length > 0 && (
                        <PostDetailRelatedV2
                            posts={(similarPosts || []).slice(9)}
                            defaultExpanded
                            showHeading
                        />
                    )}
                {isMobile && (
                    <ForumCommentAdd
                        canAdd={canAdd}
                        anonymousMode={isMyAnonymousPost}
                        hasBlueToolbar={!isMyAnonymousPost}
                        isEdit={false}
                        isMobile={isMobile}
                        isIOS={isIOS}
                        textareaRef={this.handleTextareaRef}
                        isAskAnExpert={isAskAnExpert}
                        post={post}
                        showContestAnswers={showContestAnswers}
                        isContest={isContest}
                        isBlogsComment={false}
                        isGroupsComment={false}
                        requestUser={requestUser}
                        requestUserPermissions={requestUserPermissions}
                        initialValues={initialValues}
                        currentValues={currentValues}
                        onAddCommentUnauthenticated={this.handleOnAddCommentUnauthenticated}
                        onSave={this.handleOnSaveComment}
                    />
                )}
                {isMobile && post && (
                    <PostDetailRelated
                        isAskAnExpert={viewType === PostDetailViewType.AskAnExpert}
                        isContest={isContest}
                        isMobile={isMobile}
                        location={location}
                        post={post}
                        comments={comments}
                        requestUser={requestUser}
                        similarPosts={similarPosts}
                        recentPosts={recentPosts}
                        backlinksBox={
                            postCanHavePregnancyNewsletter && userCanHavePregnancyNewsletter
                                ? backlinksBox &&
                                  removeLinesWithLinksToArticle(
                                      backlinksBox,
                                      ARTICLE_IDS_TO_HIDE_WHEN_PREGNANCY_NEWSLETTER_IS_SHOWN,
                                  )
                                : backlinksBox
                        }
                        linkedReviewArticles={linkedReviewArticles}
                        forumRelatedV2={forumRelatedV2}
                    />
                )}
            </div>
        );
    }

    private determineBannerPosition() {
        if (this.bannerPositionBRef.current) {
            const rect = this.bannerPositionBRef.current.getBoundingClientRect();
            const isBannerAtPositionA = rect.top >= window.innerHeight;
            if (this.state.isBannerAtPositionA !== isBannerAtPositionA) {
                this.setState({ isBannerAtPositionA });
            }
        }
    }

    private handleOnAddCommentUnauthenticated = () => {
        const { onAddCommentUnauthenticated, location } = this.props;
        onAddCommentUnauthenticated(location);
    };

    private renderHelmet() {
        const { baseUrl, pageRef, numberOfPages, post } = this.props;

        if (!post || post.deletedAt || post.topic.deletedAt) {
            return null;
        }

        const hasPageNums: boolean =
            numberOfPages !== null && pageRef !== null && isNumber(pageRef);

        return (
            <Helmet>
                {hasPageNums && (pageRef as number) > 1 && (
                    <link
                        rel="prev"
                        href={
                            baseUrl +
                            postDetailUrl(post, removePage1({ page: (pageRef as number) - 1 }))
                        }
                    />
                )}
                {hasPageNums && (pageRef as number) < numberOfPages && (
                    <link
                        rel="next"
                        href={baseUrl + postDetailUrl(post, { page: (pageRef as number) + 1 })}
                    />
                )}

                {(post.messagePhotos?.map((mp) => mp.photofile) || post.photos).map((photo) => [
                    <meta
                        key="url"
                        property="og:image:url"
                        content={photoUrl(
                            photo,
                            largestAvailablePhotoVersionOfPost(photo, "s1600x1600"),
                        )}
                    />,
                    <meta key="width" property="og:image:width" content={photo.width.toString()} />,
                    <meta
                        key="height"
                        property="og:image:height"
                        content={photo.height.toString()}
                    />,
                ])}
            </Helmet>
        );
    }

    private canAddComment() {
        const { post, requestUser, contestAlreadyParticipated, requestUserPermissions, viewType } =
            this.props;

        const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") !== -1;
        const isAskAnExpert = viewType === PostDetailViewType.AskAnExpert;
        const isContest = viewType === PostDetailViewType.Contest;

        return (
            isAuthenticated(requestUser) &&
            post &&
            !post.deletedAt &&
            !post.topic.deletedAt &&
            post.topic.category &&
            (!post.locked || isAdmin) &&
            (!isContest || !contestAlreadyParticipated || isAdmin) &&
            !isAskAnExpert
        );
    }

    private showContestAnswers() {
        const { contest, contestAlreadyParticipated, viewType } = this.props;

        const isContest = viewType === PostDetailViewType.Contest;

        return (
            !isContest ||
            contestAlreadyParticipated ||
            (contest && contest.seeAnswers) ||
            (contest && new Date() > new Date(contest.endDate)) // sutaz uz nebezi
        );
    }

    private renderInit = () => {
        const {
            isMobile,
            pageRef,
            numberOfPages,
            post,
            requestUserPermissions,
            requestUser,
            location,
            routeNavigate,
        } = this.props;
        const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") !== -1;
        const isModerator =
            post &&
            isAuthenticatedAs(
                requestUser,
                post.topic?.topicModerators?.map((tm) => tm.user) || post.topic?.moderators,
            );
        const hasPager =
            (isAdmin || isModerator || (!post?.deletedAt && !post?.topic.deletedAt)) &&
            numberOfPages !== null &&
            numberOfPages > 1 &&
            pageRef !== null &&
            isNumber(pageRef);

        return (
            <React.Fragment>
                <div id="scrolltoFirstHere" />{" "}
                {/* musi byt vzdy vyrenderovane v DOMe, pouziva sa zo sagy */}
                {hasPager && (
                    <Pager
                        className={cx(styles.PostDetail__pager, styles["PostDetail__pager--top"])}
                        page={pageRef as number}
                        numberOfPages={numberOfPages}
                        position="top"
                        location={location}
                        routeNavigate={routeNavigate}
                        isMobile={isMobile}
                    />
                )}
                {
                    // vykreslit dostatok elementov, tak aby browser nezmensil scrollTop position
                    // pocas LoadingState.LOADING pretoze vyska body je mensia nez scrollTop pozicia
                    // ktoru sa snazime zachovat
                    range(10).map((i) => (
                        <CommentForumPlaceholder
                            key={`placeholder-${i}`}
                            className={cx(
                                styles.PostDetail__commentPlaceholder,
                                i === 0 &&
                                    hasPager &&
                                    styles["PostDetail__commentPlaceholder--hasAbove-Pager"],
                                i === 0 &&
                                    !hasPager &&
                                    styles["PostDetail__commentPlaceholder--hasAbove-Post"],
                            )}
                        />
                    ))
                }
                {hasPager && (
                    <Pager
                        className={cx(
                            styles.PostDetail__pager,
                            styles["PostDetail__pager--bottom"],
                        )}
                        page={pageRef as number}
                        numberOfPages={numberOfPages}
                        position="bottom"
                        location={location}
                        routeNavigate={routeNavigate}
                        isMobile={isMobile}
                    />
                )}
                <div id="scrolltoLastHere" />{" "}
                {/* musi byt vzdy vyrenderovane v DOMe, pouziva sa zo sagy */}
            </React.Fragment>
        );
    };

    private renderFailure = () => {
        const { errorMessage } = this.props;

        return (
            <Failure
                errorMessage={errorMessage}
                onRetry={this.load}
                backButtonUrl={forumTopicsUrl}
                backButtonLabel={BACK_TO_TOPICS}
            />
        );
    };

    private renderSuccess = () => {
        const {
            backlinksBox,
            comments,
            commentsComplaintOpened,
            commentsWithComplaint,
            commentToBeHighlighted,
            counselling,
            isMobile,
            isMyAnonymousPost,
            isSpider,
            location,
            routeNavigate,
            location: { hash, search },
            myAnonymousCommentsIds,
            numberOfPages,
            onDeleteComment,
            onMoveUpComment,
            onReport,
            onSendHeart,
            onToggleMenuMore,
            onTrackEdit,
            pageRef,
            requestUserPermissions,
            post,
            postCanHavePregnancyNewsletter,
            recentPosts,
            requestUser,
            similarPosts,
            userCanHavePregnancyNewsletter,
            viewType,

            promoReviewArticlesHeader,
            promoReviewArticlesMiddle,
            linkedReviewArticles,
        } = this.props;

        if (!post) {
            return null;
        }

        // if page number is null (unknown) or incorrect, then renderSuccess() should not be reached
        if (numberOfPages === null || pageRef === null) {
            throw new Error("Missing numberOfPages or pageRef");
        }

        const query = parse(this.props.location.search);

        const isAskAnExpert = viewType === PostDetailViewType.AskAnExpert;
        const isContest = viewType === PostDetailViewType.Contest;

        const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") !== -1;
        const isModerator =
            post &&
            isAuthenticatedAs(
                requestUser,
                post.topic?.topicModerators?.map((tm) => tm.user) || post.topic?.moderators,
            );
        const canAdd = this.canAddComment();
        const showContestAnswers = this.showContestAnswers();

        const hashValue = hash && hash.indexOf("-") !== -1 ? hash.split("-")[1] : null;
        const commentToBeHighlightedFromHash =
            hashValue &&
            (hashValue === "last"
                ? null /* "last" from hash should be forwarded to and handled by the state */
                : parseFloat(hashValue));
        const commentToBeHighlightedFromState =
            commentToBeHighlighted &&
            (commentToBeHighlighted === "last"
                ? comments && comments.length > 0 && comments[comments.length - 1].id
                : commentToBeHighlighted);
        let highlightedId = commentToBeHighlightedFromState
            ? commentToBeHighlightedFromState
            : commentToBeHighlightedFromHash;
        if (isNaN(highlightedId)) {
            highlightedId = null;
        }

        const hasPager =
            (isAdmin || isModerator || (!post?.deletedAt && !post?.topic.deletedAt)) &&
            numberOfPages > 1 &&
            isNumber(pageRef);
        const hasAds = post && !post.deletedAt && !post.isNoAds && !post.topic.isCommercial;

        const pregnancyNewsletterDiv = isMobile &&
            postCanHavePregnancyNewsletter &&
            userCanHavePregnancyNewsletter && (
                <div className={styles["PostDetail__pregnancyNewsletter--blue"]}>
                    <h2 className={styles["PostDetail__pregnancyNewsletter--blue__header"]}>
                        {PREGNANCY_NEWSLETTER}
                    </h2>
                    <p className={styles["PostDetail__pregnancyNewsletter--blue__description"]}>
                        {PREGNANCY_NEWSLETTER_DESCRIPTION}
                    </p>
                    <div
                        className={cx(
                            styles.PostDetail__pregnancyNewsletter__image,
                            styles[`PostDetail__pregnancyNewsletter__image--${ACTIVE_SERVER_ID}`],
                        )}
                    />
                    <PregnancyNewsletter
                        formNameKey={`forum-post-${post.slug}`}
                        noBorder
                        extraEventProps={{
                            subscribe_source: "tehu_nl_widget_forum",
                            subscribe_source_app: "forum",
                            subscribe_source_detail: `forum/${post.topic.slug}/${post.slug}`,
                        }}
                        noPregnancyCalculatorLink
                    />
                </div>
            );

        const linkToPregnancyCalculator01: string = isAuthenticated(requestUser)
            ? PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR_AUTH_01
            : PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR_01;
        const linkToPregnancyCalculator02: string = isAuthenticated(requestUser)
            ? PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR_AUTH
            : PREGNANCY_NEWSLETTER_LINK_TO_PREGNANCY_CALCULATOR;

        const pregnancyWeekBox = isMobile && postCanHavePregnancyNewsletter && (
            <Panel className={styles.PostDetail__pregnancyWeeksBox}>
                <p className={styles.PostDetail__pregnancyWeeksBox__p}>
                    {PREGNANCY_WEEKS_BOX_01}{" "}
                    <Link to={PREGNANCY_WEEKS_URL[ACTIVE_SERVER_ID]}>{PREGNANCY_WEEKS_BOX_02}</Link>
                    {". "}
                    {linkToPregnancyCalculator01}{" "}
                    <Link to={pregnancyCalculatorUrl}>{linkToPregnancyCalculator02}</Link>
                    {"."}
                </p>
            </Panel>
        );

        const modifiedBacklinksBox =
            postCanHavePregnancyNewsletter && userCanHavePregnancyNewsletter
                ? backlinksBox &&
                  removeLinesWithLinksToArticle(
                      backlinksBox,
                      ARTICLE_IDS_TO_HIDE_WHEN_PREGNANCY_NEWSLETTER_IS_SHOWN,
                  )
                : backlinksBox;

        // Note: computed on desktop too, but trivial cost
        const chunk1 = (similarPosts || []).slice(0, 4);
        const chunk2 = (similarPosts || []).slice(4, 9);
        const chunk3 = (similarPosts || []).slice(9);

        // Related posts (PostDetailRelatedV2) placement rules (A/B test variant "new" only):
        //
        // - 1st box: after 1/3 of comments, but never before the 3rd comment
        // - 2nd box: after 2/3 of comments, but never before the 10th comment
        //            and only if there are at least 10 comments
        // - 3rd box: after ForumCommentAdd, before PostDetailRelated (outside loop)
        // - All boxes are shifted away from ad slots to avoid visual clutter
        //   (min 2 comment gap between a related box and an ad)
        const isRelatedV2 = this.props.forumRelatedV2 === "new";
        const numComments = comments?.length || 0;
        let relatedIdx1 = -1;
        let relatedIdx2 = -1;
        if (isRelatedV2) {
            const activeAdIndices = hasAds
                ? [
                      ...(numComments >= 5 ? [3] : []),
                      ...(numComments >= 13 ? [11] : []),
                      ...(numComments >= 21 ? [19] : []),
                      ...(numComments >= 29 ? [27] : []),
                  ]
                : [];
            const MIN_AD_DISTANCE = 2;
            const adjustForAds = (idx: number, minIdx: number = 0): number => {
                const isTooClose = (i: number) =>
                    activeAdIndices.some((adIdx) => Math.abs(i - adIdx) <= MIN_AD_DISTANCE);
                if (!isTooClose(idx)) return idx;
                for (let offset = 1; offset <= 5; offset++) {
                    if (idx + offset < numComments && !isTooClose(idx + offset))
                        return idx + offset;
                    if (idx - offset >= minIdx && !isTooClose(idx - offset)) return idx - offset;
                }
                return idx;
            };
            relatedIdx1 =
                numComments >= 8 ? adjustForAds(Math.max(2, Math.floor(numComments / 3)), 2) : -1;
            relatedIdx2 =
                numComments >= 16
                    ? adjustForAds(Math.max(10, Math.floor((2 * numComments) / 3)), 10)
                    : -1;
        }

        return (
            <React.Fragment>
                {(isAdmin || isModerator) && commentsWithComplaint.length > 0 && (
                    <div className={styles.PostDetail__complaints}>
                        <h3 className={styles.PostDetail__complaints__heading}>
                            {REPORTED_COMMENTS}
                        </h3>
                        <ul className={styles.PostDetail__complaints__list}>
                            {commentsWithComplaint.map((comment, index) => (
                                <li key={index} className={styles.PostDetail__complaints__item}>
                                    <Link
                                        className={styles.PostDetail__complaints__link}
                                        to={{
                                            pathname: postDetailUrl(post),
                                            search: stringify(
                                                removePage1(
                                                    tupdate(
                                                        parse(search) as {
                                                            [key: string]: string | number;
                                                        },
                                                        {
                                                            page: getPageNumberInPost(comment),
                                                        },
                                                    ),
                                                ),
                                            ),
                                            hash: `comment-${comment.id}`,
                                            state: {
                                                // Comment is managing it's own scroll position
                                                dontScrollOnPush: true,
                                            },
                                        }}
                                    >
                                        <strong className={styles.PostDetail__complaints__author}>
                                            {comment.authorUser.username}
                                        </strong>
                                        &nbsp;
                                        <span className={styles.PostDetail__complaints__comment}>
                                            {truncate(getAllPlaintext(comment.textAST), {
                                                length: 120,
                                            })}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {promoReviewArticlesHeader.length > 0 && (
                    <div className={styles.PostDetail__reviewArticles}>
                        <ReviewArticles
                            articles={promoReviewArticlesHeader}
                            layout={LayoutType.COMPACT}
                            utmParams={{
                                utm_medium: "widget",
                            }}
                        />
                    </div>
                )}
                <div id="scrolltoFirstHere" />{" "}
                {/* musi byt vzdy vyrenderovane v DOMe, pouziva sa zo sagy */}
                {hasPager && (
                    <Pager
                        className={cx(styles.PostDetail__pager, styles["PostDetail__pager--top"])}
                        page={pageRef as number}
                        numberOfPages={numberOfPages}
                        position="top"
                        location={location}
                        routeNavigate={routeNavigate}
                        isMobile={isMobile}
                    />
                )}
                {comments?.length > 0 &&
                    comments.map((comment, index) => (
                        <React.Fragment key={comment.id}>
                            {hasAds && index === 3 && comments.length >= 5 && (
                                <>
                                    {Boolean(pregnancyNewsletterDiv) && comments.length < 13 ? (
                                        pregnancyNewsletterDiv
                                    ) : (
                                        <AdSlot
                                            zone="Forum - Mid 01 - Mobile"
                                            location={location}
                                            marginTopWithAd={10}
                                        />
                                    )}
                                    {comments.length < 13 && pregnancyWeekBox}
                                </>
                            )}
                            {hasAds && index === 11 && comments.length >= 13 && (
                                <>
                                    {Boolean(pregnancyNewsletterDiv) && comments.length < 21 ? (
                                        pregnancyNewsletterDiv
                                    ) : (
                                        <AdSlot
                                            zone="Forum - Mid 02 - Mobile"
                                            location={location}
                                            marginTopWithAd={10}
                                        />
                                    )}
                                    {comments.length < 21 && pregnancyWeekBox}
                                </>
                            )}
                            {hasAds && index === 19 && comments.length >= 21 && (
                                /*
                                <AdSlot
                                    zone="Forum - Rectangle 3 (Desktop)"
                                    location={location}
                                />
                                */
                                <>
                                    {Boolean(pregnancyNewsletterDiv) && comments.length < 29 ? (
                                        pregnancyNewsletterDiv
                                    ) : (
                                        <AdSlot
                                            zone="Forum - Mid 03 - Mobile"
                                            location={location}
                                            marginTopWithAd={10}
                                        />
                                    )}
                                    {comments.length < 29 && pregnancyWeekBox}
                                </>
                            )}
                            {hasAds && index === 27 && comments.length >= 29 && (
                                <>
                                    {pregnancyNewsletterDiv ? (
                                        pregnancyNewsletterDiv
                                    ) : (
                                        <AdSlot
                                            zone="Forum - Mid 04 - Mobile"
                                            location={location}
                                            marginTopWithAd={10}
                                        />
                                    )}
                                    {pregnancyWeekBox}
                                </>
                            )}

                            {isMobile &&
                                !isContest &&
                                !isAskAnExpert &&
                                index === relatedIdx1 &&
                                chunk1.length > 0 && (
                                    <PostDetailRelatedV2 posts={chunk1} initialVisible={2} />
                                )}
                            {isMobile &&
                                !isContest &&
                                !isAskAnExpert &&
                                index === relatedIdx2 &&
                                chunk2.length > 0 && (
                                    <PostDetailRelatedV2 posts={chunk2} initialVisible={3} />
                                )}

                            {/* TODO: Change entity name to match post db entity */}
                            <Complaint
                                id={comment.id}
                                entity={ComplaintTargetType.ForumMessage}
                                className={styles.PostDetail__comment__complaint}
                                scrollOffset={44 + 44 + 30 /* header + 30px spacing */}
                                inForum
                                inGroups={false}
                            />
                            <Comment
                                className={cx(
                                    styles.PostDetail__comment,
                                    index === 0 &&
                                        hasPager &&
                                        !commentsComplaintOpened[0] &&
                                        styles["PostDetail__comment--hasAbove-Pager"],
                                    index === 0 &&
                                        !hasPager &&
                                        !commentsComplaintOpened[0] &&
                                        styles["PostDetail__comment--hasAbove-Post"],
                                    commentsComplaintOpened[index] &&
                                        styles["PostDetail__comment--hasAbove-Complaint"],
                                )}
                                comment={{
                                    ...comment,
                                    // Convert message photos to photos for backward compatibility
                                    photos:
                                        comment.messagePhotos?.map((mp) => mp.photofile) ||
                                        comment.photos,
                                }}
                                post={post}
                                isMyAnonymousPost={isMyAnonymousPost}
                                isMyAnonymousComment={myAnonymousCommentsIds.includes(comment.id)}
                                isSpider={isSpider}
                                highlightTriggered={comment.id === highlightedId}
                                highlightStarted={comment.id === commentToBeHighlightedFromState}
                                requestUser={requestUser}
                                requestUserPermissions={requestUserPermissions}
                                isMobile={isMobile}
                                inForum
                                followLinks={post.topic.isCommercial || !!counselling}
                                moderators={
                                    post.topic.topicModerators?.map((tm) => tm.user) ||
                                    post.topic.moderators
                                }
                                location={location}
                                routeNavigate={routeNavigate}
                                hasBodyHiddenText={!showContestAnswers}
                                hasBodyDeletedStatus={!post.deletedAt}
                                hasFooterLike={showContestAnswers && !post.topic.deletedAt}
                                hasFooterLikeDisabled={isMyAnonymousPost}
                                hasFooterReply={!isContest && canAdd}
                                hasMenu={!post.topic.deletedAt}
                                hasMenuReport={!isMyAnonymousPost && !isContest}
                                hasMenuEdit={!isContest || isAdmin}
                                hasMenuDelete={comment.canDelete && (!isContest || isAdmin)}
                                hasMenuMoveUp={post.topic.id === TESTINGS_TOPIC_ID}
                                onReply={this.handleOnCommentReply}
                                onReport={onReport}
                                onToggleMenuMore={onToggleMenuMore}
                                onDelete={onDeleteComment}
                                onTrackEdit={onTrackEdit}
                                onMoveUp={onMoveUpComment}
                                onSendHeart={this.handleOnSendHeart}
                                onDidHighlight={this.handleOnDidHighlighted}
                                counsellor={post.counsellingCounsellor}
                            />
                            {promoReviewArticlesMiddle.length > 0 && index === 11 && (
                                <div className={styles.PostDetail__reviewArticles}>
                                    <ReviewArticles
                                        headerLogo
                                        header={WIKI_REVIEWS}
                                        headerLinkTo={`/${WIKI_REVIEWS_ARTICLE_SLUG}/?utm_medium=widget`}
                                        articles={promoReviewArticlesMiddle}
                                        layout={LayoutType.HORIZONTAL}
                                        utmParams={{
                                            utm_medium: "widget",
                                        }}
                                    />
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                {hasPager && (
                    <Pager
                        className={cx(
                            styles.PostDetail__pager,
                            styles["PostDetail__pager--bottom"],
                        )}
                        page={pageRef as number}
                        numberOfPages={numberOfPages}
                        position="bottom"
                        location={location}
                        routeNavigate={routeNavigate}
                        isMobile={isMobile}
                    />
                )}
                <div id="scrolltoLastHere" />{" "}
                {/* musi byt vzdy vyrenderovane v DOMe, pouziva sa zo sagy */}
                {comments?.length > 0 && isAskAnExpert && counselling && (
                    <div className={styles.PostDetail__ctaBtn}>
                        <Btn
                            type={BtnType.Blue}
                            label={
                                !counselling.isArchived
                                    ? FORUM_ADD_QUESTION
                                    : COUNSELLING_IS_ARCHIVED
                            }
                            disabled={counselling.isArchived}
                            onClick={this.handleOnAddCounsellingQuestion}
                        />
                    </div>
                )}
            </React.Fragment>
        );
    };

    private isEditable = () => {
        const postTime = new Date(this.props.post.created);
        const commentTimeWithDelay = new Date(
            new Date(postTime).setMinutes(postTime.getMinutes() + 2),
        );
        const currentTime = new Date(Date.now());
        return commentTimeWithDelay > currentTime;
    };

    private load = () => {
        const {
            onLoad,
            routeParams: { topicSlug, postSlug },
            comments,
            location,
            pageRef,
        } = this.props;
        const numCommentsBeforeLoad = comments?.length || 0;

        onLoad(topicSlug, postSlug, pageRef, location, numCommentsBeforeLoad);
    };

    private handleTextareaRef = (ref: HTMLTextAreaElement) => {
        this.textArea = ref;
    };

    private handleOnPostReply = () => {
        const { onReply, post, location } = this.props;
        const username = isAnonymousUser(post.author)
            ? getAnonymousUserPseudonym(ANONYMOUS_PSEUDONYM_AUTHOR_NAME)
            : post.author.username;
        onReply(username, this.textArea, null, location);
    };

    private handleOnAddCounsellingQuestion = () => {
        const { counselling, onAddCounsellingQuestion, location } = this.props;
        onAddCounsellingQuestion(counselling, location);
    };

    private handleOnCommentReply = (username: string, repliedToCommentId: number) => {
        const { onReply, location } = this.props;
        onReply(username, this.textArea, repliedToCommentId, location);
    };

    private handleOnSaveComment = (values: CommentFormData) => {
        const { onAddComment, post, location } = this.props;
        onAddComment(post.id, values, location);
    };

    private handleOnDidHighlighted = () => {
        const { onToggleCommentHighlight, post } = this.props;
        const reducerKey = `${post.topic.slug}/${post.slug}`;
        onToggleCommentHighlight(reducerKey, null);
    };

    private handleOnSendHeart = () => {
        const { onSendHeart, location } = this.props;
        onSendHeart(location);
    };
}

export default PostDetail;
